import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Me } from '@wo/shared';
import { api, errorText } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button, Card, ErrorText, Field, Input, Loading, PageHeader, Table } from '../../components/ui';

interface DeviceRow {
  id: number;
  name: string;
  enrolledAt: string;
  lastSeenAt: string | null;
  revokedAt: string | null;
}

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;

export function DevicesPage({ me }: { me: Me }) {
  const qc = useQueryClient();
  const rows = useQuery({ queryKey: ['devices'], queryFn: () => api.get<DeviceRow[]>('/api/devices') });
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const enroll = async () => {
    setError(null);
    try {
      await api.post('/api/devices', { name });
      // This browser is now a shared mechanic iPad; the manager has been signed out of it.
      window.location.assign('/m');
    } catch (e) {
      setError(errorText(e));
    }
  };
  const revoke = async (id: number) => {
    if (!confirm('Revoke this iPad? Anyone signed in on it is signed out, and it can no longer be used until enrolled again.')) return;
    try {
      await api.post(`/api/devices/${id}/revoke`);
      qc.invalidateQueries({ queryKey: ['devices'] });
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <>
      <PageHeader title="Devices" />
      <div className="grid gap-4 lg:grid-cols-[380px_1fr]">
        <Card title="Enroll this iPad">
          <p className="mb-3 text-sm text-slate-700">
            Easiest: the mechanic opens the site on the new iPad, taps <b>Mechanic? Sign in with your PIN</b> and sends a request. It shows at the top of every page here; approve it if the code matches.
          </p>
          <p className="mb-2 text-sm font-medium text-slate-700">Or enroll it yourself, on the iPad:</p>
          <ol className="mb-3 list-decimal space-y-1 pl-5 text-sm text-slate-700">
            <li>On the iPad, open the site in Safari, tap <b>Share → Add to Home Screen</b>.</li>
            <li>Open it from the <b>Home Screen icon</b>, sign in with Google here, and come to this page.</li>
            <li>Name it and tap Enroll. You'll be signed out and it becomes the mechanics' iPad.</li>
          </ol>
          {!isStandalone() && (
            <p className="mb-3 rounded-md bg-amber-50 p-2 text-sm text-amber-900">
              You're in a browser tab, not the Home Screen app. On iPad, enroll from the Home Screen app — Safari and the app keep separate sign-ins.
            </p>
          )}
          <ErrorText error={error} />
          <Field label="iPad name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Shop iPad 1, Field truck 3…" /></Field>
          <Button className="mt-3 w-full" disabled={!name.trim()} onClick={enroll}>Enroll this iPad</Button>
        </Card>
        <Card title="Enrolled iPads">
          {rows.isLoading ? <Loading /> : (
            <Table head={['Name', 'Enrolled', 'Last seen', '']} empty={rows.data?.length === 0}>
              {rows.data?.map((d) => (
                <tr key={d.id} className={d.revokedAt ? 'text-slate-400' : ''}>
                  <td className="px-3 py-2 font-medium">{d.name}</td>
                  <td className="px-3 py-2">{dateTime(d.enrolledAt)}</td>
                  <td className="px-3 py-2">{dateTime(d.lastSeenAt)}</td>
                  <td className="px-3 py-2 text-right">
                    {d.revokedAt ? `Revoked ${dateTime(d.revokedAt)}` : me.role === 'ADMIN' && <Button variant="ghost" onClick={() => revoke(d.id)}>Revoke</Button>}
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      </div>
    </>
  );
}
